import { useCallback, useEffect, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  Field,
  Input,
  Modal,
  Notice,
  Panel,
  Select,
  Spinner,
  Table,
  TableWrap,
  Tabs,
  Textarea,
  Toolbar,
  useToast,
} from 'iboon-ui';

import { api, type Category, type Person, type StockItem } from '../api';

/**
 * What a new starter is given, and what a leaver hands back.
 *
 * A kit is the list written down once. Onboarding is otherwise something
 * somebody keeps in their head and gets wrong on a busy week -- and what gets
 * forgotten is never the laptop, it is the second charger and the dock.
 *
 * Offboarding is deliberately not a list of its own. It is what the register
 * already knows somebody holds, read back, so it cannot drift from what was
 * really handed over.
 */
type KitLine = {
  id: string;
  quantity: number;
  note: string;
  categoryId: string | null;
  categoryName: string | null;
  stockItemId: string | null;
  stockItemName: string | null;
};

type Kit = {
  id: string;
  name: string;
  description: string;
  active: boolean;
  lineCount: number;
  lines: KitLine[];
};

type AvailabilityLine = KitLine & {
  available: number;
  choices: Array<{ id: string; name: string; assetTag: string | null; serialNumber: string | null }>;
};

export function Onboarding() {
  const [tab, setTab] = useState('kits');

  return (
    <>
      <Toolbar>
        <Tabs
          items={[
            { id: 'kits', label: 'Kits' },
            { id: 'leaving', label: 'Somebody leaving' },
          ]}
          value={tab}
          onChange={setTab}
          label="Onboarding"
        />
      </Toolbar>

      {tab === 'kits' ? <Kits /> : <Offboarding />}
    </>
  );
}

/* --------------------------------------------------------------- kits -- */

function Kits() {
  const [kits, setKits] = useState<Kit[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Kit | 'new' | null>(null);
  const [issuing, setIssuing] = useState<Kit | null>(null);

  const load = useCallback(async () => {
    const result = await api.get<{ kits: Kit[] }>('/api/kits?includeInactive=true');
    setKits(result.kits);
  }, []);

  useEffect(() => {
    load().catch((caught: unknown) =>
      setError(caught instanceof Error ? caught.message : 'Could not load kits.'),
    );
  }, [load]);

  if (error) {
    return (
      <Notice tone="danger" title="Could not load kits">
        {error}
      </Notice>
    );
  }

  return (
    <>
      <div className="row-actions" style={{ marginBottom: 12 }}>
        <Button variant="primary" onClick={() => setEditing('new')}>
          Make a kit
        </Button>
      </div>

      {!kits ? (
        <Spinner />
      ) : kits.length === 0 ? (
        <EmptyState
          title="No kits yet"
          description="A kit is what somebody starting a particular job is given — a laptop, a dock, two cables. Write it down once and it stops being remembered wrong."
        />
      ) : (
        kits.map((kit) => (
          <Panel
            key={kit.id}
            title={
              <>
                {kit.name} {!kit.active && <Badge tone="neutral">not used any more</Badge>}
              </>
            }
          >
            {kit.description && <p className="sub">{kit.description}</p>}

            {kit.lines.length === 0 ? (
              <p className="muted">Nothing in it yet.</p>
            ) : (
              kit.lines.map((line) => (
                <div key={line.id} className="line">
                  <span>
                    {line.categoryName ? (
                      <>
                        {/* A kit says "a laptop", not which laptop -- that is
                            chosen on the day from whatever is free. */}
                        A {line.categoryName.replace(/s$/, '')}
                        <div className="sub">any one that is free</div>
                      </>
                    ) : (
                      <>
                        {line.stockItemName}
                        <div className="sub">from stock</div>
                      </>
                    )}
                  </span>
                  <span className="num">× {line.quantity}</span>
                </div>
              ))
            )}

            <div className="row-actions" style={{ marginTop: 12 }}>
              <Button variant="primary" disabled={kit.lines.length === 0} onClick={() => setIssuing(kit)}>
                Give this to somebody
              </Button>
              <Button variant="ghost" onClick={() => setEditing(kit)}>
                Edit
              </Button>
            </div>
          </Panel>
        ))
      )}

      {editing && (
        <EditKit
          kit={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load();
          }}
        />
      )}

      {issuing && (
        <IssueKit
          kit={issuing}
          onClose={() => setIssuing(null)}
          onDone={() => {
            setIssuing(null);
            void load();
          }}
        />
      )}
    </>
  );
}

function EditKit({
  kit,
  onClose,
  onSaved,
}: {
  kit: Kit | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(kit?.name ?? '');
  const [description, setDescription] = useState(kit?.description ?? '');
  const [active, setActive] = useState(kit?.active ?? true);
  const [lines, setLines] = useState<Array<{ categoryId: string; stockItemId: string; quantity: number }>>(
    (kit?.lines ?? []).map((line) => ({
      categoryId: line.categoryId ?? '',
      stockItemId: line.stockItemId ?? '',
      quantity: line.quantity,
    })),
  );
  const [categories, setCategories] = useState<Category[]>([]);
  const [stock, setStock] = useState<StockItem[]>([]);
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  useEffect(() => {
    api
      .get<{ categories: Category[] }>('/api/categories')
      .then((r) => setCategories(r.categories.filter((c) => c.tracking === 'tracked')))
      .catch(() => {});
    api.get<{ items: StockItem[] }>('/api/stock').then((r) => setStock(r.items)).catch(() => {});
  }, []);

  async function save() {
    setBusy(true);
    try {
      let id = kit?.id;
      if (id) {
        await api.patch(`/api/kits/${id}`, { name, description, active });
      } else {
        ({ id } = await api.post<{ id: string }>('/api/kits', { name, description }));
      }

      // Sent whole rather than diffed: editing a kit is somebody saying what
      // the list should be, and the list is a dozen rows.
      await api.put(`/api/kits/${id}/lines`, {
        lines: lines
          .filter((line) => line.categoryId || line.stockItemId)
          .map((line) => ({
            categoryId: line.categoryId || null,
            stockItemId: line.categoryId ? null : line.stockItemId || null,
            quantity: line.quantity,
          })),
      });

      toast.show(kit ? 'Saved.' : `${name} is ready to give out.`);
      onSaved();
    } catch (caught) {
      toast.show(caught instanceof Error ? caught.message : 'Could not save it.', { tone: 'danger' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open size="md" title={kit ? `Edit ${kit.name}` : 'Make a kit'} onClose={onClose}>
      <Field label="Name" required hint="The job, usually: “Developer”, “Designer”.">
        <Input value={name} onChange={(event) => setName(event.target.value)} />
      </Field>
      <Field label="What it is for">
        <Textarea rows={2} value={description} onChange={(event) => setDescription(event.target.value)} />
      </Field>

      {kit && (
        <Field label="Still given out" hint="Turning this off keeps the kit and stops offering it.">
          <Select value={active ? 'yes' : 'no'} onChange={(event) => setActive(event.target.value === 'yes')}>
            <option value="yes">Yes</option>
            <option value="no">No — retired</option>
          </Select>
        </Field>
      )}

      <div style={{ marginTop: 18 }}>
        <div className="about-panel__head">
          <strong>What is in it</strong>
          <Button
            variant="ghost"
            onClick={() => setLines((was) => [...was, { categoryId: '', stockItemId: '', quantity: 1 }])}
          >
            Add a line
          </Button>
        </div>

        {lines.length === 0 ? (
          <p className="muted" style={{ marginTop: 8 }}>
            Nothing yet.
          </p>
        ) : (
          lines.map((line, index) => (
            <div key={index} className="catalog-field">
              <Select
                value={line.categoryId ? `c:${line.categoryId}` : line.stockItemId ? `s:${line.stockItemId}` : ''}
                onChange={(event) => {
                  const [kind, id] = event.target.value.split(':');
                  setLines((was) =>
                    was.map((l, i) =>
                      i === index
                        ? {
                            ...l,
                            categoryId: kind === 'c' ? (id ?? '') : '',
                            stockItemId: kind === 's' ? (id ?? '') : '',
                          }
                        : l,
                    ),
                  );
                }}
              >
                <option value="">Choose something…</option>
                <optgroup label="A thing of this kind, whichever is free">
                  {categories.map((category) => (
                    <option key={category.id} value={`c:${category.id}`}>
                      {category.name}
                    </option>
                  ))}
                </optgroup>
                <optgroup label="From stock">
                  {stock.map((item) => (
                    <option key={item.id} value={`s:${item.id}`}>
                      {item.name}
                    </option>
                  ))}
                </optgroup>
              </Select>
              <Input
                type="number"
                min={1}
                value={String(line.quantity)}
                onChange={(event) =>
                  setLines((was) =>
                    was.map((l, i) =>
                      i === index ? { ...l, quantity: Math.max(1, Number(event.target.value)) } : l,
                    ),
                  )
                }
              />
              <span />
              <Button variant="ghost" onClick={() => setLines((was) => was.filter((_, i) => i !== index))}>
                Remove
              </Button>
            </div>
          ))
        )}
      </div>

      <div className="row-actions" style={{ marginTop: 18, justifyContent: 'flex-start' }}>
        <Button variant="primary" disabled={busy || !name.trim()} onClick={() => void save()}>
          {busy ? 'Saving…' : kit ? 'Save' : 'Make it'}
        </Button>
        <Button variant="ghost" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
      </div>
    </Modal>
  );
}

/**
 * Handing a kit over.
 *
 * The availability check is the point: a kit that lists a laptop is no use on
 * the morning somebody starts if there is not one on the shelf, and finding
 * that out by opening another page is how a starter goes a fortnight without
 * a dock.
 */
function IssueKit({ kit, onClose, onDone }: { kit: Kit; onClose: () => void; onDone: () => void }) {
  const [lines, setLines] = useState<AvailabilityLine[] | null>(null);
  const [chosen, setChosen] = useState<Record<string, string>>({});
  const [people, setPeople] = useState<Person[]>([]);
  const [personId, setPersonId] = useState('');
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  useEffect(() => {
    api
      .get<{ lines: AvailabilityLine[] }>(`/api/kits/${kit.id}/availability`)
      .then((r) => {
        setLines(r.lines);
        // Pre-picked, so the common case is one click. Still changeable.
        setChosen(
          Object.fromEntries(
            r.lines.filter((line) => line.choices.length > 0).map((line) => [line.id, line.choices[0]!.id]),
          ),
        );
      })
      .catch(() => setLines([]));
    api
      .get<{ people: Person[] }>('/api/people')
      .then((r) => {
        const active = r.people.filter((p) => p.active);
        setPeople(active);
        setPersonId(active[0]?.id ?? '');
      })
      .catch(() => {});
  }, [kit.id]);

  const short = (lines ?? []).filter((line) => line.available < line.quantity);

  async function issue() {
    setBusy(true);
    try {
      const result = await api.post<{ given: string[] }>(`/api/kits/${kit.id}/issue`, {
        personId,
        assetIds: Object.values(chosen).filter(Boolean),
      });
      toast.show(`Given: ${result.given.join(', ')}`);
      onDone();
    } catch (caught) {
      toast.show(caught instanceof Error ? caught.message : 'Could not give it out.', { tone: 'danger' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open size="md" title={`Give out the ${kit.name} kit`} onClose={onClose}>
      {people.length === 0 ? (
        <Notice tone="accent" title="Nobody to give it to">
          The directory is empty. Refresh it from identity on the People page first.
        </Notice>
      ) : (
        <>
          <Field label="Who is starting" required>
            <Select value={personId} onChange={(event) => setPersonId(event.target.value)}>
              {people.map((person) => (
                <option key={person.id} value={person.id}>
                  {person.name} — {person.email}
                </option>
              ))}
            </Select>
          </Field>

          {!lines ? (
            <Spinner />
          ) : (
            <>
              {short.length > 0 && (
                <Notice tone="danger" title={`Not enough of ${short.length === 1 ? 'one thing' : `${short.length} things`}`}>
                  {short.map((line) => `${line.categoryName ?? line.stockItemName}`).join(', ')} — buy
                  more, or give out what there is and the rest later.
                </Notice>
              )}

              {lines.map((line) => (
                <Field
                  key={line.id}
                  label={`${line.categoryName ?? line.stockItemName} × ${line.quantity}`}
                  hint={`${line.available} free`}
                >
                  {line.categoryName ? (
                    line.choices.length === 0 ? (
                      <Input value="none free" disabled />
                    ) : (
                      <Select
                        value={chosen[line.id] ?? ''}
                        onChange={(event) =>
                          setChosen((was) => ({ ...was, [line.id]: event.target.value }))
                        }
                      >
                        <option value="">Do not give one</option>
                        {line.choices.map((choice) => (
                          <option key={choice.id} value={choice.id}>
                            {choice.name}
                            {choice.assetTag ? ` — ${choice.assetTag}` : ''}
                          </option>
                        ))}
                      </Select>
                    )
                  ) : (
                    <Input value={`${line.quantity} taken from stock`} disabled />
                  )}
                </Field>
              ))}
            </>
          )}

          <div className="row-actions" style={{ marginTop: 14, justifyContent: 'flex-start' }}>
            <Button variant="primary" disabled={busy || !personId || !lines} onClick={() => void issue()}>
              {busy ? 'Handing over…' : 'Hand it over'}
            </Button>
            <Button variant="ghost" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
}

/* --------------------------------------------------------- offboarding -- */

type Holding = {
  person: { id: string; name: string; email: string; active: boolean };
  assets: Array<{ id: string; name: string; assetTag: string | null; categoryName: string; condition: string }>;
  stock: Array<{ id: string; name: string; holding: number }>;
};

function Offboarding() {
  const [people, setPeople] = useState<Person[]>([]);
  const [personId, setPersonId] = useState('');
  const [holding, setHolding] = useState<Holding | null>(null);
  const [confirming, setConfirming] = useState(false);
  const toast = useToast();

  useEffect(() => {
    api.get<{ people: Person[] }>('/api/people').then((r) => setPeople(r.people)).catch(() => {});
  }, []);

  const load = useCallback(async (id: string) => {
    if (!id) return setHolding(null);
    setHolding(await api.get<Holding>(`/api/kits/offboarding/${id}`));
  }, []);

  useEffect(() => {
    void load(personId);
  }, [personId, load]);

  async function returnAll() {
    try {
      const result = await api.post<{ assets: number; stock: number }>(
        `/api/kits/offboarding/${personId}/return-all`,
        { condition: 'good', notes: 'offboarding' },
      );
      toast.show(`${result.assets} things and ${result.stock} kinds of stock are back.`);
      setConfirming(false);
      await load(personId);
    } catch (caught) {
      toast.show(caught instanceof Error ? caught.message : 'Could not take it back.', { tone: 'danger' });
    }
  }

  const nothing = holding && holding.assets.length === 0 && holding.stock.length === 0;

  return (
    <>
      <Field label="Who is leaving" hint="Everything the register says they have, ready to ask for back.">
        <Select value={personId} onChange={(event) => setPersonId(event.target.value)}>
          <option value="">Choose somebody…</option>
          {people.map((person) => (
            <option key={person.id} value={person.id}>
              {person.name} — {person.email}
              {person.active ? '' : ' (already marked as left)'}
            </option>
          ))}
        </Select>
      </Field>

      {personId && !holding && <Spinner />}

      {holding && (
        <>
          {nothing ? (
            <EmptyState
              title={`${holding.person.name} has nothing`}
              description="Either they were never given anything, or it has all come back."
            />
          ) : (
            <>
              <Panel title={`${holding.person.name} has these`}>
                {holding.assets.map((asset) => (
                  <div key={asset.id} className="line">
                    <span>
                      {asset.name}
                      <div className="sub">
                        {[asset.assetTag, asset.categoryName].filter(Boolean).join(' · ')}
                      </div>
                    </span>
                    <span className="sub">{asset.condition}</span>
                  </div>
                ))}
                {holding.stock.map((item) => (
                  <div key={item.id} className="line">
                    <span>
                      {item.name}
                      <div className="sub">from stock</div>
                    </span>
                    <span className="num">× {item.holding}</span>
                  </div>
                ))}
              </Panel>

              <div className="row-actions">
                <Button variant="primary" onClick={() => setConfirming(true)}>
                  Take it all back
                </Button>
              </div>
            </>
          )}
        </>
      )}

      {confirming && holding && (
        <ConfirmDialog
          open
          title={`Take everything back from ${holding.person.name}?`}
          confirmLabel="Take it all back"
          onConfirm={() => void returnAll()}
          onClose={() => setConfirming(false)}
        >
          <p>
            {holding.assets.length} {holding.assets.length === 1 ? 'thing goes' : 'things go'} back on
            the shelf and {holding.stock.length}{' '}
            {holding.stock.length === 1 ? 'kind of stock returns' : 'kinds of stock return'} to the
            pool. The record of them having had it is kept.
          </p>
        </ConfirmDialog>
      )}
    </>
  );
}
